import { useState, useEffect, useRef, useCallback } from 'react';

import { useToast } from './use-toast';

/**
 * Hook for debounced auto-save functionality
 * @param {Function} saveFn - Async function that performs the save
 * @param {number} delay - Delay in milliseconds before auto-save triggers (default: 5000ms)
 * @returns {Object} { isDirty, isSaving, scheduleAutosave, saveNow, cancelAutosave }
 */
export function useAutoSave(saveFn, delay = 5000) {
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const timeoutRef = useRef(null);
  const saveFnRef = useRef(saveFn);
  const { toast } = useToast();

  // Keep saveFn ref updated to avoid stale closures
  useEffect(() => {
    saveFnRef.current = saveFn;
  }, [saveFn]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const scheduleAutosave = useCallback(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsDirty(true);

    // Schedule new save
    timeoutRef.current = setTimeout(async () => {
      setIsSaving(true);
      try {
        await saveFnRef.current();
        setIsDirty(false);
        toast({
          title: "Saved",
          duration: 2000
        });
      } catch (error) {
        console.error('Auto-save failed:', error);
        toast({
          title: "Save failed",
          description: error.message || "Please try again",
          variant: "destructive",
          duration: 3000
        });
      } finally {
        setIsSaving(false);
      }
    }, delay);
  }, [delay, toast]);

  const saveNow = useCallback(async () => {
    // Clear any pending auto-save
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (!isDirty && !isSaving) return;

    setIsSaving(true);
    try {
      await saveFnRef.current();
      setIsDirty(false);
      toast({
        title: "Saved",
        duration: 2000
      });
    } catch (error) {
      console.error('Save failed:', error);
      toast({
        title: "Save failed",
        description: error.message || "Please try again",
        variant: "destructive",
        duration: 3000
      });
    } finally {
      setIsSaving(false);
    }
  }, [isDirty, isSaving, toast]);

  const cancelAutosave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const markClean = useCallback(() => {
    setIsDirty(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  return {
    isDirty,
    isSaving,
    scheduleAutosave,
    saveNow,
    cancelAutosave,
    markClean
  };
}
